import type {ReactElement} from 'react';
import {useContext, useEffect, useMemo, useRef} from 'react';
import React from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  EditableTheme,
  EventWizardDescriptionMarkdownProps,
  getWordNearCursor,
} from '@Components/event-wizard/event-wizard-description-markdown/event-wizard-description-markdown.types';
import {MARKDOWN_EDITOR_LINK_TOOLTIP_TIMER} from '@Components/event-wizard/event-wizard-description-markdown/event-wizard-description-markdown.types';
import styles from './event-wizard-description-markdown.module.scss';
import './markdown-custom-style.scss';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import Tooltip from 'react-bootstrap/Tooltip';

export function EventWizardDescriptionMarkdown({description, onUpdate, isErrorState = false}: EventWizardDescriptionMarkdownProps): ReactElement<any> {
  const {isExtraSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);
  const quillRef = useRef<ReactQuill | null>(null);
  const wrapperContainerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return (): void => {
      if (tooltipTimerRef.current !== null) {
        window.clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  const cleanupTooltip = (): void => {
    if (tooltipTimerRef.current) {
      window.clearTimeout(tooltipTimerRef.current);
    }

    if (tooltipRef.current) {
      tooltipRef.current.classList.add('_hidden');
    }
  };

  const placeTooltipOverLinkBtn = (): void => {
    const linkBtn = wrapperContainerRef.current?.querySelector('.ql-link');

    if (!linkBtn || !tooltipRef.current) {
      return;
    }

    const linkBtnRect = linkBtn.getBoundingClientRect();
    const tooltipTop = linkBtnRect.top - 8;
    const tooltipLeft = linkBtnRect.left + linkBtnRect.width / 2;

    cleanupTooltip();
    tooltipRef.current.classList.remove('_hidden');
    tooltipRef.current.style.top = `${tooltipTop}px`;
    tooltipRef.current.style.left = `${tooltipLeft}px`;
    tooltipTimerRef.current = window.setTimeout(cleanupTooltip, MARKDOWN_EDITOR_LINK_TOOLTIP_TIMER);
  };

  const showLinkTextInputPopup = (linkText: string): void => {
    const quill = quillRef.current?.getEditor();

    if (!quill) {
      return;
    }

    (quill.theme as EditableTheme).tooltip?.edit('link', linkText);
  }

  const onToolbarHyperlinkButtonClick = (): void => {
    const quill = quillRef.current?.getEditor();
    const range = quill?.getSelection();
    const hasSelectedText = !!range && range.length > 0;

    if (!quill || !range) {
      placeTooltipOverLinkBtn();
      return;
    }

    if (hasSelectedText) {
      const selectedText = quill.getText(range.index, range.length);
      showLinkTextInputPopup(selectedText);
      return;
    }

    const nearbyWord = getWordNearCursor(quill.getText(), range.index);

    if (!nearbyWord) {
      placeTooltipOverLinkBtn();
      return;
    }

    quill.setSelection(nearbyWord.index, nearbyWord.length, 'user');
    showLinkTextInputPopup(nearbyWord.text);
  };

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: [[{header: [2, 3, false]}], ['bold', 'italic', 'underline'], [{list: 'ordered'}, {list: 'bullet'}], ['link']],
        handlers: {
          link: onToolbarHyperlinkButtonClick,
        },
      },
    };
  }, []);

  const formats = ['header', 'bold', 'italic', 'underline', 'list', 'link'];

  return (
    <div ref={wrapperContainerRef} className="_position-relative">
      <Tooltip ref={tooltipRef} id="event-description-link-tooltip" placement="top" className={`_rounded-arrow _pointer-events-none _position-fixed ${styles.linkToolTip} _hidden`}>
        {window.i18next.t('pmwjs_please_select_text_to_add_a_link')}
      </Tooltip>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        className={`${styles.container} ${isExtraSmallScreen ? 'xsmall-mobile' : ''} ${isErrorState ? '-error' : ''} spacing-m-b-3`}
        value={description}
        modules={modules}
        formats={formats}
        onChange={(value: string) => {
          onUpdate(value);
        }}
      />
    </div>
  );
}
